import {
  Eye,
  Hash,
  Microphone,
  MusicNotes,
  Play,
  Plus,
  TextT,
} from '@phosphor-icons/react';
import { Checkbox, cn } from '@reelplicate/ui';
import type { ComponentType } from 'react';

import { assetMeta, captionCount, formatDuration, STATUS, typeLabel } from './library';
import type { Asset, AssetStatus, MatchKind, SearchSignal } from './types';

/** Figma "Scrim": darkens the top and bottom edges so the pills stay legible. */
const SCRIM =
  'linear-gradient(180deg, rgba(38,39,25,0.35) 0%, rgba(38,39,25,0) 28%, rgba(38,39,25,0) 70%, rgba(38,39,25,0.55) 100%)';

/** Waveform bar heights (px) from Figma "Brand track — Morning" (185:656). */
const WAVEFORM = [
  46, 51, 20, 29, 43, 47, 17, 74, 53, 41, 32, 67, 58, 17, 47, 28, 59, 23, 51, 46, 24, 16,
  78, 80,
];
const WAVEFORM_PLAYED = 9;

const MATCH_ICONS: Record<MatchKind, ComponentType<{ className?: string }>> = {
  seen: Eye,
  said: Microphone,
  onscreen: TextT,
  tag: Plus,
  text: Hash,
};

const pill =
  'flex items-center rounded-full px-[7px] py-0.5 text-caption whitespace-nowrap';
const darkPill = cn(pill, 'bg-[rgba(38,39,25,0.6)] text-text-inverse');

export function StatusPill({
  status,
  bordered,
  className,
}: {
  status: AssetStatus;
  bordered?: boolean;
  className?: string;
}) {
  return (
    <span
      className={cn(
        pill,
        'bg-bg-surface text-text-primary gap-[5px]',
        bordered && 'border-border-subtle border',
        className,
      )}
    >
      <span aria-hidden className={cn('size-1.5 rounded-full', STATUS[status].dot)} />
      {STATUS[status].label}
    </span>
  );
}

/** Figma "Media / Match reason" (184:1449): why a search result matched. */
export function MatchReason({
  signal,
  className,
}: {
  signal: SearchSignal;
  className?: string;
}) {
  const Icon = MATCH_ICONS[signal.kind];
  return (
    <span
      className={cn(
        'bg-brand-primary text-caption text-text-on-brand flex w-fit max-w-full items-center gap-[5px] rounded-full py-[3px] pr-2 pl-1.5',
        className,
      )}
    >
      <Icon aria-hidden className="size-3 shrink-0" />
      <span className="truncate">{signal.label}</span>
    </span>
  );
}

type MediaTileProps = {
  asset: Asset;
  selected: boolean;
  /** Something is selected: every checkbox shows, not just on hover. */
  selecting: boolean;
  onSelectedChange: (selected: boolean) => void;
  onOpen: () => void;
  /** Search results: why this one matched. */
  reason?: SearchSignal;
};

/**
 * Figma "Media / Tile" (184:189): a 9:16 thumb with its type, duration and
 * status, then title and meta. Captions show their copy instead of a thumb,
 * audio a waveform. Hover shows the select checkbox (and Play on video);
 * selected tiles get a Primary Green frame.
 */
export function MediaTile({
  asset,
  selected,
  selecting,
  onSelectedChange,
  onOpen,
  reason,
}: MediaTileProps) {
  const isCaption = asset.kind === 'caption';
  const isAudio = asset.kind === 'audio';
  const playable = asset.kind === 'reel' || asset.kind === 'clip';

  return (
    <div className="group/tile relative flex min-w-0 flex-col">
      <button
        type="button"
        onClick={onOpen}
        aria-label={[
          asset.title,
          typeLabel(asset),
          asset.status && STATUS[asset.status].label,
        ]
          .filter(Boolean)
          .join(', ')}
        className="focus-visible:shadow-focus-ring flex min-w-0 flex-col gap-2 rounded-lg text-left outline-none"
      >
        <div
          className={cn(
            'relative aspect-[197/350] w-full overflow-hidden rounded-lg',
            isCaption
              ? 'bg-bg-surface border-border-subtle border'
              : isAudio
                ? 'bg-bg-inverse'
                : 'bg-bg-muted',
          )}
        >
          {asset.thumbnail && !isCaption && (
            <img
              src={asset.thumbnail}
              alt=""
              className="absolute inset-0 size-full object-cover"
            />
          )}
          {!isCaption && (
            <div
              aria-hidden
              className="absolute inset-0"
              style={{ backgroundImage: SCRIM }}
            />
          )}

          <span
            className={cn(
              'absolute top-2 left-2',
              isCaption ? cn(pill, 'bg-bg-muted text-text-primary') : darkPill,
            )}
          >
            {typeLabel(asset)}
          </span>

          {isAudio && (
            <>
              <MusicNotes
                aria-hidden
                className="text-brand-primary absolute top-[24%] left-1/2 size-[22px] -translate-x-1/2"
              />
              <div
                aria-hidden
                className="absolute top-[38.5%] right-4 left-4 flex items-center gap-[3px] overflow-hidden"
              >
                {WAVEFORM.map((h, i) => (
                  <span
                    key={i}
                    className={cn(
                      'w-1 shrink-0 rounded-[2px]',
                      i < WAVEFORM_PLAYED ? 'bg-brand-primary' : 'bg-bg-emphasis',
                    )}
                    style={{ height: h }}
                  />
                ))}
              </div>
            </>
          )}

          {isCaption && (
            <>
              <p className="text-body-sm text-text-primary absolute top-[43px] right-3 left-3 line-clamp-[10] whitespace-pre-line">
                {asset.caption}
              </p>
              {asset.status && (
                <StatusPill
                  status={asset.status}
                  bordered
                  className="absolute right-2 bottom-10"
                />
              )}
              <div className="bg-bg-subtle absolute inset-x-0 bottom-0 flex items-center gap-1.5 px-3 py-2">
                <Hash aria-hidden className="text-icon-secondary size-3.5" />
                <span className="text-caption text-text-secondary flex-1">
                  {captionCount(asset.caption ?? '')}
                </span>
                <span className="text-overline text-text-tertiary uppercase">EN</span>
              </div>
            </>
          )}

          {!isCaption && (
            <div className="absolute inset-x-2 bottom-2 flex flex-col gap-2">
              {reason && <MatchReason signal={reason} />}
              <div className="flex items-center justify-between gap-2">
                {asset.duration !== undefined ? (
                  <span className={darkPill}>{formatDuration(asset.duration)}</span>
                ) : (
                  <span />
                )}
                {asset.status && <StatusPill status={asset.status} />}
              </div>
            </div>
          )}
          {isCaption && reason && (
            <MatchReason signal={reason} className="absolute bottom-[68px] left-2" />
          )}

          {playable && (
            <span
              aria-hidden
              className="absolute top-1/2 left-1/2 flex size-11 -translate-1/2 items-center justify-center rounded-full bg-white/92 opacity-0 transition-opacity duration-[120ms] group-hover/tile:opacity-100"
            >
              <Play weight="fill" className="text-text-primary size-[18px]" />
            </span>
          )}
          {selected && (
            <span
              aria-hidden
              className="border-brand-primary pointer-events-none absolute inset-0 rounded-lg border-3"
            />
          )}
        </div>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-body-sm-strong text-text-primary truncate">
            {asset.title}
          </span>
          <span className="text-caption text-text-secondary truncate">
            {assetMeta(asset)}
          </span>
        </div>
      </button>

      <Checkbox
        checked={selected}
        onCheckedChange={(value) => onSelectedChange(value === true)}
        aria-label={`Select ${asset.title}`}
        className={cn(
          'absolute top-2 right-2 transition-opacity duration-[120ms]',
          selected || selecting
            ? 'opacity-100'
            : 'opacity-0 group-hover/tile:opacity-100 focus-visible:opacity-100',
        )}
      />
    </div>
  );
}
